// src/components/common/FeedbackWidget.jsx
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { server } from "../../constants";

const CATEGORIES = [
  "General",
  "Bug / Technical Issue",
  "Academic",
  "Facilities",
  "Staff / Teacher",
  "Fees & Payments",
  "Transport",
  "Suggestion",
  "Other",
];

const FeedbackWidget = () => {
  const { user, isAuthenticated } = useSelector((s) => s.auth || {});
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll on mobile when open
  useEffect(() => {
    if (open && isMobile) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, isMobile]);

  if (!isAuthenticated || !user) return null;

  const resetAndClose = () => {
    setOpen(false);
    setCategory(CATEGORIES[0]);
    setMessage("");
  };

  const submit = async () => {
    if (!message.trim()) return toast.error("Please write your feedback");
    setSaving(true);
    try {
      await axios.post(
        server + "/feedback",
        { category, message: message.trim(), page_url: location.pathname },
        { withCredentials: true },
      );
      toast.success("Feedback submitted!");
      resetAndClose();
    } catch (err) {
      toast.error(
        err.response ? err.response.data.message : "Failed to submit",
      );
    } finally {
      setSaving(false);
    }
  };

  const iS = {
    width: "100%",
    padding: "10px 12px",
    border: "1.5px solid #E2E8F0",
    borderRadius: 10,
    fontSize: "0.88rem",
    background: "#F8FAFC",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#0F172A",
  };

  return (
    <>
      <style>{`
        .fb-btn { -webkit-tap-highlight-color: transparent; }
        .fb-sheet {
          transform: translateY(100%);
          transition: transform 0.3s cubic-bezier(0.34,1.1,0.64,1);
        }
        .fb-sheet.open { transform: translateY(0); }
        .fb-backdrop {
          opacity: 0; pointer-events: none;
          transition: opacity 0.25s ease;
        }
        .fb-backdrop.open { opacity: 1; pointer-events: all; }
      `}</style>

      {/* Floating Button */}
      <button
        className="fb-btn"
        onClick={() => setOpen(true)}
        title="Send Feedback"
        style={{
          position: "fixed",
          right: 18,
          bottom: isMobile ? 76 : 18, // above bottom nav bar on mobile
          zIndex: 1500,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "#4F46E5",
          color: "#fff",
          border: "none",
          boxShadow: "0 6px 18px rgba(79,70,229,0.45)",
          cursor: "pointer",
          fontSize: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        💬
      </button>

      {/* Backdrop */}
      <div
        className={"fb-backdrop" + (open ? " open" : "")}
        onClick={resetAndClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 2000,
          backdropFilter: "blur(2px)",
        }}
      />

      {isMobile ? (
        /* ── Mobile: Bottom Sheet ── */
        <div
          className={"fb-sheet" + (open ? " open" : "")}
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2001,
            background: "#fff",
            borderRadius: "20px 20px 0 0",
            paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
            maxHeight: "88vh",
            overflowY: "auto",
          }}
        >
          {/* Handle */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "12px 0 6px",
            }}
          >
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "#E2E8F0",
              }}
            />
          </div>
          <div style={{ padding: "6px 20px 8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontWeight: 800,
                  color: "#0F172A",
                  fontSize: "1rem",
                  margin: 0,
                }}
              >
                💬 Send Feedback
              </h3>
              <button
                onClick={resetAndClose}
                style={{
                  background: "#F1F5F9",
                  border: "none",
                  borderRadius: 8,
                  width: 32,
                  height: 32,
                  cursor: "pointer",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>
            <label
              style={{
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#64748B",
                marginBottom: 5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ ...iS, marginBottom: 14 }}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label
              style={{
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#64748B",
                marginBottom: 5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Your Feedback
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Tell us what's on your mind..."
              style={{
                ...iS,
                resize: "none",
                marginBottom: 16,
                lineHeight: 1.6,
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={resetAndClose}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#fff",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: 12,
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#475569",
                  fontFamily: "inherit",
                  fontSize: "0.88rem",
                }}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                style={{
                  flex: 2,
                  padding: "12px",
                  background: "#4F46E5",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  cursor: "pointer",
                  fontWeight: 700,
                  opacity: saving ? 0.6 : 1,
                  fontFamily: "inherit",
                  fontSize: "0.88rem",
                }}
              >
                {saving ? "Sending..." : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ── Desktop: Center Modal ── */
        open && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 2001,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                width: "100%",
                maxWidth: 420,
                padding: 24,
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                pointerEvents: "all",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <h3
                  style={{
                    fontWeight: 800,
                    color: "#0F172A",
                    fontSize: "1.05rem",
                    margin: 0,
                  }}
                >
                  💬 Send Feedback
                </h3>
                <button
                  onClick={resetAndClose}
                  style={{
                    background: "#F1F5F9",
                    border: "none",
                    borderRadius: 8,
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>
              <label
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#64748B",
                  marginBottom: 4,
                }}
              >
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{ ...iS, marginBottom: 14 }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <label
                style={{
                  display: "block",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#64748B",
                  marginBottom: 4,
                }}
              >
                Your Feedback
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="Tell us what's on your mind..."
                style={{ ...iS, resize: "vertical", marginBottom: 18 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={resetAndClose}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#fff",
                    border: "1.5px solid #E2E8F0",
                    borderRadius: 10,
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#475569",
                    fontFamily: "inherit",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#4F46E5",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    cursor: "pointer",
                    fontWeight: 700,
                    opacity: saving ? 0.6 : 1,
                    fontFamily: "inherit",
                  }}
                >
                  {saving ? "Sending..." : "Send Feedback"}
                </button>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default FeedbackWidget;
